import { useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { ExternalLink, Github, X, Calendar, Users, CheckCircle, AlertTriangle } from 'lucide-react';
import AnimatedBackground from './AnimatedBackground';

const projects = [
    {
      id: 10,
      title: "SQL Survival Game",
      description: "An interactive, story-based web game that makes learning SQL fun and engaging through survival scenarios. Players progress through three levels (Beach, Jungle, Ruins) by solving SQL challenges from beginner to intermediate level. Features built-in SQL editor, instant query validation, and achievement system.",
      technologies: ["JavaScript", "SQL", "Game Design", "Interactive Learning", "Web Development"],
      image: "/images/sql.png",
      links: { live: "https://sql-survival-game.vercel.app/", github: "https://github.com/16Prithvi/sql-survival-game" },
      color: "#4ecdc4",
      year: "2025",
      teamSize: "1",
      features: ["3 Adventure Levels", "SQL Query Editor", "Instant Validation", "Achievement System"],
      challenges: ["Game Balance", "SQL Challenge Design", "Interactive Storytelling"]
    },
    {
      id: 4,
      title: "NutriTrack",
      description: "Full-stack nutrition tracking website allowing users to log meals, track nutrients, set dietary goals, and receive personalized recommendations. Features include barcode scanning, meal planning, progress visualization, and social sharing capabilities.",
      technologies: ["React", "Node.js", "MongoDB", "Express", "RESTful API"],
      image: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?q=80&w=1200&auto=format&fit=crop",
      links: { live: "#", github: "https://github.com/16Prithvi/NutriTrack" },
      color: "#8b00ff",
      year: "2025",
      teamSize: "1",
      features: ["Meal Logging", "Barcode Scanning", "Progress Tracking", "Social Sharing"],
      challenges: ["Nutrition Database", "User Experience", "Mobile Responsiveness"]
    },
    {
      id: 9,
      title: "Smart Logistics Tracker",
      description: "A comprehensive logistics management system designed to streamline supply chain operations with real-time tracking, automated notifications, and intelligent route optimization. Features include package tracking, delivery scheduling, inventory management, and performance analytics.",
      technologies: ["React", "Node.js", "MongoDB", "Real-time APIs", "Map Integration"],
      image: "/images/Smart-Logistics.jpg",
      links: { live: "#", github: "https://github.com/16Prithvi/Smart-Logistics" },
      color: "#ff6b35",
      year: "2024",
      teamSize: "1",
      features: ["Real-time Tracking", "Route Optimization", "Automated Notifications", "Performance Analytics"],
      challenges: ["Real-time Updates", "GPS Integration", "Scalability", "User Interface Design"]
    },
    {
      id: 7,
      title: "FinVista (SIP Calculator)",
      description: "Comprehensive financial tool for systematic investment planning with built-in advisory capabilities. Calculates returns, provides investment recommendations based on risk profiles, and visualizes long-term growth projections. Includes tax-saving investment options.",
      technologies: ["JavaScript", "Chart.js", "Financial Algorithms", "PWA", "Local Storage"],
      image: "/images/finvista.png",
      links: { live: "#", github: "https://github.com/16Prithvi" },
      color: "#4ecdc4",
      year: "2024",
      teamSize: "1",
      features: ["SIP Calculations", "Risk Assessment", "Growth Projections", "Tax Optimization"],
      challenges: ["Financial Accuracy", "User Interface", "Data Security"]
    },
    {
      id: 8,
      title: "Outcome-Based Result Analyzer",
      description: "AI-powered educational outcome analysis tool using natural language processing to assess student performance, identify learning gaps, and recommend personalized improvement strategies. Integrates with existing educational management systems.",
      technologies: ["NLP", "Python", "GPT", "Educational Tech", "Data Analytics"],
      image: "https://images.unsplash.com/photo-1501504905252-473c47e087f8?q=80&w=1200&auto=format&fit=crop",
      links: { live: "#", github: "https://github.com/16Prithvi" },
      color: "#a8e6cf",
      year: "2024",
      teamSize: "4",
      features: ["Performance Analysis", "Learning Gap Detection", "Personalized Recommendations", "System Integration"],
      challenges: ["NLP Accuracy", "Educational Data", "Privacy Concerns"]
    },
    {
      id: 1,
      title: "Hospital Management System",
      description: "A comprehensive hospital management solution built with data structures and algorithms. Features include patient record management, appointment scheduling, inventory tracking, and staff management. Implemented efficient data structures for optimized search and retrieval operations.",
      technologies: ["DSA", "Java", "MySQL", "Team Project", "UI/UX"],
      image: "/images/hospital.webp",
      links: { live: "#", github: "https://github.com/16Prithvi" },
      color: "#00ff41",
      year: "2023",
      teamSize: "4",
      features: ["Patient Records", "Appointment Scheduling", "Inventory Management", "Staff Management"],
      challenges: ["Database Optimization", "Real-time Updates", "User Interface Design"]
    },
    {
      id: 2,
      title: "Forest Fire Detection using YOLOv11",
      description: "Advanced forest fire detection system using YOLOv11 computer vision model to identify fires in satellite and drone imagery. Features early warning alerts, fire spread prediction, and integration with emergency response systems. Achieved 95% detection accuracy in real-world tests.",
      technologies: ["Machine Learning", "Python", "YOLOv11", "Computer Vision", "TensorFlow"],
      image: "/images/forest-fire.jpg",
      links: { live: "#", github: "https://github.com/16Prithvi" },
      color: "#00ffff",
      year: "2024",
      teamSize: "4",
      features: ["Real-time Detection", "Early Warning System", "Fire Spread Prediction", "Emergency Integration"],
      challenges: ["Model Accuracy", "Real-time Processing", "Integration with Emergency Systems"]
    },
    {
      id: 3,
      title: "Manufacturing Analysis for Spicer",
      description: "Manufacturing process analysis system for Spicer industries using computer vision and ML to detect defects, predict maintenance needs, and optimize production workflows. Reduced defect rates by 30% and maintenance costs by 25% through predictive analytics.",
      technologies: ["Machine Learning", "OpenCV", "Python", "Industrial IoT", "Data Analysis"],
      image: "/images/spicer.jpg",
      links: { live: "#", github: "https://github.com/16Prithvi" },
      color: "#ff0080",
      year: "2024",
      teamSize: "4",
      features: ["Defect Detection", "Predictive Maintenance", "Production Optimization", "Real-time Analytics"],
      challenges: ["Industrial Integration", "Data Processing", "Model Deployment"]
    },
    {
      id: 5,
      title: "StockMate",
      description: "B2B SaaS inventory management platform designed for small to medium businesses. Features include real-time stock tracking, supplier management, automated reordering, sales forecasting, and comprehensive analytics dashboard. Integrates with popular e-commerce platforms.",
      technologies: ["React", "TypeScript", "Firebase", "SaaS", "Cloud Functions"],
      image: "https://images.unsplash.com/photo-1507925921958-8a62f3d1a50d?q=80&w=1200&auto=format&fit=crop",
      links: { live: "#", github: "https://github.com/16Prithvi" },
      color: "#ffff00",
      year: "2024",
      teamSize: "1",
      features: ["Real-time Tracking", "Supplier Management", "Automated Reordering", "Analytics Dashboard"],
      challenges: ["Scalability", "Integration", "User Onboarding"]
    },
    {
      id: 6,
      title: "Eco Map",
      description: "Interactive weather analysis dashboard with real-time climate data visualization, historical trends, and environmental impact assessment. Features include customizable data views, alerts for extreme weather events, and climate change trend analysis.",
      technologies: ["React", "D3.js", "Weather API", "Leaflet", "Responsive Design"],
      image: "https://images.unsplash.com/photo-1601134467661-3d775b999c8b?q=80&w=1200&auto=format&fit=crop",
      links: { live: "#", github: "https://github.com/16Prithvi" },
      color: "#ff6b6b",
      year: "2023",
      teamSize: "1",
      features: ["Real-time Data", "Interactive Maps", "Weather Alerts", "Climate Trends"],
      challenges: ["API Integration", "Data Visualization", "Performance Optimization"]
    },
];

type ProjectType = 'All' | 'Web' | 'ML' | 'Backend';

export default function Projects() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] = useState<ProjectType>('All');

  // Categorize projects
  const getProjectType = (project: typeof projects[0]): ProjectType => {
    const techString = project.technologies.join(' ').toLowerCase();
    if (techString.includes('machine learning') || techString.includes('yolo') || techString.includes('opencv') || techString.includes('tensorflow') || techString.includes('nlp') || techString.includes('computer vision')) {
      return 'ML';
    }
    if (techString.includes('react') || techString.includes('next.js') || techString.includes('frontend') || techString.includes('javascript') || techString.includes('typescript') || techString.includes('html') || techString.includes('css') || techString.includes('d3.js') || techString.includes('leaflet')) {
      return 'Web';
    }
    if (techString.includes('node.js') || techString.includes('backend') || techString.includes('express') || techString.includes('api') || techString.includes('mysql') || techString.includes('mongodb') || techString.includes('firebase')) {
      return 'Backend';
    }
    return 'Web'; // Default to Web
  };

  const filteredProjects = activeFilter === 'All' 
    ? projects 
    : projects.filter(p => getProjectType(p) === activeFilter);

  const filters: ProjectType[] = ['All', 'Web', 'ML', 'Backend'];

  const selectedProject = selectedId ? projects.find(p => p.id === selectedId) : null;

  return (
    <section id="projects" ref={ref} className="relative py-20 min-h-screen bg-black overflow-hidden">
      {/* Animated Background */}
      <AnimatedBackground variant="particles" intensity="low" />
      <AnimatedBackground variant="stars" intensity="medium" />
      
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-display font-black mb-4">
            <span className="glitch-text text-neon-blue neon-glow" data-text="PROJECTS">
              PROJECTS
            </span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-neon-blue to-neon-pink mx-auto mb-8" />
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Showcasing innovative solutions and cutting-edge technologies across various domains
          </p>
        </motion.div>

        {/* Filter Pills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          {filters.map((filter) => (
            <motion.button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`relative px-6 py-2 rounded-full font-mono text-sm transition-all duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-neon-green focus-visible:outline-offset-2 ${
                activeFilter === filter
                  ? 'text-white'
                  : 'text-gray-400 hover:text-gray-300 bg-dark-800/50 border border-gray-700/50'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {activeFilter === filter && (
                <motion.div
                  layoutId="activeFilter"
                  className="absolute inset-0 bg-gradient-to-r from-neon-blue via-cyan-300 to-neon-pink rounded-full"
                  initial={false}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}
              <span className="relative z-10">{filter}</span>
            </motion.button>
          ))}
        </motion.div>

        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((project, index) => (
            <motion.div
              key={project.id}
              layoutId={`card-container-${project.id}`}
              layout
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ duration: 0.4, delay: index * 0.05, type: 'spring', stiffness: 100 }}
              onClick={() => setSelectedId(project.id)}
              className="group relative cursor-pointer"
              style={{ borderRadius: '0.75rem' }}
              whileHover={{ y: -5 }}
            >
              <motion.div 
                className="relative bg-dark-800/50 backdrop-blur-md rounded-xl overflow-hidden border border-neon-green/20 hover:border-neon-green/50 transition-all duration-500 hover:shadow-xl hover:shadow-neon-green/20 h-96"
                whileHover={{ 
                  boxShadow: `0 0 30px ${project.color}40`,
                  borderColor: `${project.color}80`
                }}
              >
                <motion.div layoutId={`card-image-container-${project.id}`} className="relative h-48 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent opacity-70" />
                </motion.div>
                <div className="p-6">
                  <motion.h3 layoutId={`card-title-${project.id}`} className="text-xl font-display font-bold text-white mb-2 group-hover:text-neon-green transition-colors">
                    {project.title}
                  </motion.h3>
                  <p className="text-gray-300 text-sm mb-4 line-clamp-3 leading-relaxed">
                    {project.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.slice(0, 4).map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className="px-2 py-1 text-xs font-mono rounded border"
                        style={{
                          backgroundColor: `${project.color}1A`,
                          borderColor: `${project.color}4D`,
                          color: project.color
                        }}
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        <AnimatePresence>
          {selectedId && selectedProject && (
            <div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                onClick={() => setSelectedId(null)}
                className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[1000]"
              />
              <div className="fixed inset-0 z-[1001] flex items-start justify-center pt-24 p-4">
                {/* MODAL STRUCTURE FIX: Added flex flex-col to make the content area scrollable */}
                <motion.div
                  layoutId={`card-container-${selectedProject.id}`}
                  className="relative w-full max-w-3xl max-h-full bg-dark-800 rounded-xl border border-neon-green/30 shadow-2xl shadow-neon-green/20 overflow-hidden flex flex-col"
                >
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { delay: 0.3 } }}
                    exit={{ opacity: 0 }}
                    onClick={() => setSelectedId(null)}
                    className="absolute top-4 right-4 p-2 bg-red-500/50 rounded-full hover:bg-red-500/70 transition-colors z-20"
                  >
                    <X className="text-white" size={20} />
                  </motion.button>

                  {/* Image is a direct child */}
                  <motion.div layoutId={`card-image-container-${selectedProject.id}`} className="relative h-64 w-full overflow-hidden flex-shrink-0">
                    <img src={selectedProject.image} alt={selectedProject.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-dark-800 via-dark-800/50 to-transparent" />
                  </motion.div>
                  
                  {/* SCROLLING CONTENT: Added overflow-y-auto to this div */}
                  <div className="p-8 overflow-y-auto">
                    <motion.h2 layoutId={`card-title-${selectedProject.id}`} className="text-4xl font-display font-bold text-white mb-2">
                      {selectedProject.title}
                    </motion.h2>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0, transition: { delay: 0.2, duration: 0.4 } }}
                      exit={{ opacity: 0 }}
                    >
                      <p className="text-gray-300 mb-6">{selectedProject.description}</p>
                      
                      <div className="flex items-center space-x-8 mb-6 border-y border-white/10 py-3">
                        <div className="flex items-center space-x-2">
                          <Calendar className="text-neon-green" size={18} />
                          <span className="text-sm text-gray-300 font-mono">{selectedProject.year}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Users className="text-neon-blue" size={18} />
                          <span className="text-sm text-gray-300 font-mono">{selectedProject.teamSize} Member(s)</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                        <div>
                          <h4 className="flex items-center gap-2 text-lg font-mono font-bold text-neon-green mb-3">
                            <CheckCircle size={18} /> Key Features
                          </h4>
                          <ul className="space-y-2">
                            {selectedProject.features.map((feature, i) => (
                              <li key={i} className="flex items-start space-x-3">
                                <div className="w-1.5 h-1.5 bg-neon-green rounded-full mt-1.5" />
                                <span className="text-sm text-gray-300">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="flex items-center gap-2 text-lg font-mono font-bold text-neon-pink mb-3">
                            <AlertTriangle size={18} /> Challenges
                          </h4>
                          <ul className="space-y-2">
                            {selectedProject.challenges.map((challenge, i) => (
                              <li key={i} className="flex items-start space-x-3">
                                <div className="w-1.5 h-1.5 bg-neon-pink rounded-full mt-1.5" />
                                <span className="text-sm text-gray-300">{challenge}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        <motion.a
                          href={selectedProject.links.live}
                          target="_blank" rel="noopener noreferrer"
                          whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                          className="flex items-center gap-2 px-4 py-2 bg-neon-green text-dark-900 font-bold rounded-lg hover:bg-opacity-90 transition-all"
                        >
                          <ExternalLink size={16} /> Live Demo
                        </motion.a>
                        <motion.a
                          href={selectedProject.links.github}
                          target="_blank" rel="noopener noreferrer"
                          whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                          className="flex items-center gap-2 px-4 py-2 bg-neon-blue text-dark-900 font-bold rounded-lg hover:bg-opacity-90 transition-all"
                        >
                          <Github size={16} /> Source Code
                        </motion.a>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}